// Library Methods
import i18next from "i18next";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Alert from "src/components/shared/alerts/Alert/Alert";
import { useTheme } from "@mui/material/styles";
import {
  DownloadIcon,
  HeartbeatIcon,
  SpeechBubbleIcon,
  ViewFileIcon,
} from "src/assets/icons";

import examCardWithDeviceImg from "src/assets/images/operating-manual/instructions-for-use/exam-card-with-device.png";
import examCardWithDeviceFrImg from "src/assets/images/operating-manual/instructions-for-use/exam-card-with-device-fr.png";
import completedExamCardImg from "src/assets/images/operating-manual/instructions-for-use/completed-exam-card.png";
import completedExamCardFrImg from "src/assets/images/operating-manual/instructions-for-use/completed-exam-card-fr.png";

// Config
import { priorityLanguageMap } from "src/config/languages";

// Styles
import { ModalCustomCancel } from "src/styles/mui/muiStylesHelper";
import { UI_SIZES } from "src/constants/uiTokens";

const LiveMonitorResults = () => {
  const { t } = useTranslation("operating-manual");
  const theme = useTheme();

  const currentLang = i18next.language as keyof typeof priorityLanguageMap;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <Grid container spacing="20px">
        <Grid item xs={12} sm={6}>
          <Typography>
            1. {t("Once the exam is linked, you can click the ")}
            <IconButton
              sx={{
                width: `${UI_SIZES.iconButtonSize}px`,
                height: `${UI_SIZES.iconButtonSize}px`,
                color: "#FFF",
                backgroundColor: "#202338",
                borderRadius: "4px",
                pointerEvents: "none",
              }}
            >
              <HeartbeatIcon />
            </IconButton>
            {t(
              " screen button next to the exam or the device card to monitor the patient's progress."
            )}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={
                currentLang === "fr"
                  ? examCardWithDeviceFrImg
                  : examCardWithDeviceImg
              }
              width="100%"
              alt="exam-card-with-device"
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing="20px">
        <Grid item xs={12} sm={6}>
          <Typography>
            2. {t("Click on the ")}
            <IconButton
              sx={{
                width: `${UI_SIZES.iconButtonSize}px`,
                height: `${UI_SIZES.iconButtonSize}px`,
                color: "#FFF",
                backgroundColor: "#202338",
                borderRadius: "4px",
                pointerEvents: "none",
              }}
            >
              <ViewFileIcon />
            </IconButton>
            {t(
              " next to the patient's exam to view results. This is accessible in the patient page by searching for the patient, and through the dashboard if the exam was recently completed."
            )}
          </Typography>
          <Typography mt="20px">
            3.{" "}
            <span style={{ color: "#0066CC", fontWeight: 600 }}>
              {t("Optional")}:{" "}
            </span>
            <span>{t("Click on the ")}</span>
            <IconButton
              sx={{
                width: `${UI_SIZES.iconButtonSize}px`,
                height: `${UI_SIZES.iconButtonSize}px`,
                color: "#FFF",
                backgroundColor: "#202338",
                borderRadius: "4px",
                pointerEvents: "none",
              }}
            >
              <SpeechBubbleIcon />
            </IconButton>
            {t(" comment button to add a comment to the report.")}
          </Typography>

          <Box
            sx={{
              mt: "20px",
            }}
          >
            <Typography>
              4. {t("Click on the ")}
              <Button
                sx={{
                  ...ModalCustomCancel(theme),
                  pointerEvents: "none",
                }}
                startIcon={<DownloadIcon />}
              >
                PDF
              </Button>
              {t(" download button to download a PDF version of the results.")}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <img
              src={
                currentLang === "fr"
                  ? completedExamCardFrImg
                  : completedExamCardImg
              }
              width="100%"
              alt="completed-exam-card"
            />
          </Box>
        </Grid>
      </Grid>

      <Alert
        variant="info"
        alertContent={t(
          "Note: The VR device will go into sleep mode if it is not being worn for a period of time to preserve battery. Once the test is complete, we recommend you plug in the VR device. If you have no more patients for the day, we recommend you turn the VR device and the controllers off."
        )}
      />
    </Box>
  );
};

export default LiveMonitorResults;
